import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { CheckCircle, CircleNotch, Warning, ArrowRight } from '@phosphor-icons/react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('id');
  const txId = searchParams.get('tx_id');
  const planId = searchParams.get('plan_id');
  const [status, setStatus] = useState('verifying');
  const [planName, setPlanName] = useState('');
  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verify = async () => {
      if (!paymentId) {
        setStatus('error');
        return;
      }
      try {
        const res = await api.post('/subscription/verify-payment', {
          payment_id: paymentId,
          tx_id: txId,
          plan_id: planId,
        });
        if (res.data.status === 'paid') {
          setStatus('success');
          setPlanName(res.data.plan_name || planId);
        } else {
          setStatus('failed');
        }
      } catch {
        setStatus('error');
      }
    };
    verify();
  }, [paymentId, txId, planId]);

  return (
    <div className="min-h-screen bg-[#F7F7F7] flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="bg-white border border-black/15 p-10 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <CircleNotch size={48} weight="bold" className="text-[#FF5A00] mx-auto mb-4 animate-spin" />
            <h1 className="font-sans text-2xl font-black mb-2">Verifying Payment...</h1>
            <p className="font-mono text-sm text-[#5E5E5E]">Please wait while we confirm your payment</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={48} weight="bold" className="text-[#00A859] mx-auto mb-4" />
            <h1 className="font-sans text-2xl font-black mb-2">Payment Successful!</h1>
            <p className="font-mono text-sm text-[#5E5E5E] mb-2">
              Your <span className="text-[#FF5A00] font-bold uppercase">{planName}</span> plan is now active.
            </p>
            <p className="font-mono text-xs text-[#8F8F8F] mb-8">You can now access all features included in your plan.</p>
            <Link
              to="/dashboard"
              data-testid="go-to-dashboard-btn"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-8 py-3 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all shadow-[4px_4px_0px_rgba(0,0,0,1)]"
            >
              Go to Dashboard <ArrowRight size={16} weight="bold" />
            </Link>
          </>
        )}
        {(status === 'error' || status === 'failed') && (
          <>
            <Warning size={48} weight="bold" className="text-[#FF2B2B] mx-auto mb-4" />
            <h1 className="font-sans text-2xl font-black mb-2">Payment Issue</h1>
            <p className="font-mono text-sm text-[#5E5E5E] mb-8">
              {status === 'failed' ? 'Your payment was not completed. Please try again.' : 'There was an issue verifying your payment.'}
            </p>
            <Link
              to="/billing"
              data-testid="back-to-billing-btn"
              className="inline-flex items-center gap-2 border border-black text-black px-8 py-3 font-mono text-sm uppercase tracking-wide hover:bg-black hover:text-white transition-all"
            >
              Back to Billing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
